import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useFacilities, useFacility } from "@/hooks/use-facilities";
import { useMonitoringLocations } from "@/hooks/use-monitoring-locations";
import { useFacilityPermits } from "@/hooks/use-permits";
import { useCreateSamplingEvent } from "@/hooks/use-sampling-events";
import { useOrgMembers } from "@/hooks/use-org-members";
import { useToast } from "@/hooks/use-toast";
import {
  PageHeader,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  FormField,
  Combobox,
  Select,
  inputClass,
} from "@/components/ui";

type SamplingEventType = components["schemas"]["SamplingEventType"];

interface SearchParams {
  scheduleId?: string;
  date?: string;
  facilityId?: string;
}

export const Route = createFileRoute("/_authenticated/sampling-events/new")({
  component: CreateSamplingEventPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    scheduleId: typeof search.scheduleId === "string" ? search.scheduleId : undefined,
    date: typeof search.date === "string" ? search.date : undefined,
    facilityId: typeof search.facilityId === "string" ? search.facilityId : undefined,
  }),
});

const eventTypes: SamplingEventType[] = [
  "routine",
  "complaint",
  "follow_up",
  "special",
  "self_monitoring",
  "emergency",
  "unscheduled",
];

function CreateSamplingEventPage() {
  const { t } = useTranslation(["sampling", "common"]);
  const navigate = useNavigate();
  const toast = useToast();
  const { scheduleId, date, facilityId: urlFacilityId } = Route.useSearch();

  const orgId = useAppStore((s) => s.activeOrgId);

  // Form state
  const [facilityId, setFacilityId] = useState(urlFacilityId ?? "");
  const [facilitySearch, setFacilitySearch] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [permitId, setPermitId] = useState("");
  const [_permitSearch, setPermitSearch] = useState("");
  const [eventType, setEventType] = useState<SamplingEventType>("routine");
  const [scheduledDate, setScheduledDate] = useState(date ?? "");
  const [compliancePeriodStart, setCompliancePeriodStart] = useState("");
  const [compliancePeriodEnd, setCompliancePeriodEnd] = useState("");
  const [weatherConditions, setWeatherConditions] = useState("");
  const [fieldNotes, setFieldNotes] = useState("");
  const [collectedBy, setCollectedBy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Data queries
  const { data: facilitiesData, isFetching: facilitiesFetching } = useFacilities(orgId, {
    limit: 20,
    status: "active",
    search: facilitySearch || undefined,
  });
  const facilities = facilitiesData?.data ?? [];

  const { data: selectedFacility } = useFacility(orgId, facilityId);
  const selectedFacilityLabel = facilityId
    ? (facilities.find((f) => f.id === facilityId)?.name ?? selectedFacility?.name)
    : undefined;

  const { data: locationsData, isFetching: locationsFetching } = useMonitoringLocations(
    orgId,
    facilityId,
    {
      limit: 20,
      status: "active",
      search: locationSearch || undefined,
    },
  );
  const locations = locationsData?.data ?? [];

  const { data: permitsData, isFetching: permitsFetching } = useFacilityPermits(orgId, facilityId, {
    limit: 20,
  });
  const permits = permitsData?.data ?? [];

  const { data: membersData } = useOrgMembers(orgId);
  const members = membersData?.members ?? [];

  const createMutation = useCreateSamplingEvent(orgId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !facilityId || !locationId) return;

    setSubmitting(true);
    createMutation.mutate(
      {
        facility_id: facilityId,
        monitoring_location_id: locationId,
        event_type: eventType,
        scheduled_date: scheduledDate || undefined,
        permit_id: permitId || null,
        sample_schedule_id: scheduleId || null,
        compliance_period_start: compliancePeriodStart || null,
        compliance_period_end: compliancePeriodEnd || null,
        weather_conditions: weatherConditions || null,
        field_notes: fieldNotes || null,
        collected_by: collectedBy || null,
      },
      {
        onSuccess: (data) => {
          toast.success("sampling:createTitle");
          void navigate({
            to: "/sampling-events/$eventId",
            params: { eventId: data.id },
          });
        },
        onError: () => {
          toast.error("sampling:errors.createFailed");
          setSubmitting(false);
        },
      },
    );
  };

  const eventTypeOptions = eventTypes.map((et) => ({
    value: et,
    label: t(`eventTypes.${et}`),
  }));

  const showCompliancePeriod = eventType === "routine" || eventType === "self_monitoring";

  if (!orgId) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-8">
      <Breadcrumbs
        items={[{ label: t("title"), to: "/sampling-events" }, { label: t("createTitle") }]}
      />
      <PageHeader title={t("createTitle")} />

      <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 space-y-6">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <FormField label={t("fields.facility")} required>
              <Combobox
                value={facilityId}
                onChange={(val) => {
                  setFacilityId(val);
                  // Don't clear location — sticky selection per design
                }}
                onSearch={setFacilitySearch}
                loading={facilitiesFetching}
                placeholder={t("filters.facility")}
                options={facilities.map((f) => ({ value: f.id, label: f.name }))}
                selectedLabel={selectedFacilityLabel}
              />
            </FormField>

            <FormField
              label={t("fields.monitoringLocation")}
              required
              hint={!facilityId ? t("selectFacilityFirst", { ns: "common" }) : undefined}
            >
              {facilityId ? (
                <Combobox
                  value={locationId}
                  onChange={setLocationId}
                  onSearch={setLocationSearch}
                  loading={locationsFetching}
                  placeholder={t("filters.monitoringLocation")}
                  options={locations.map((l) => ({ value: l.id, label: l.name }))}
                />
              ) : (
                <input
                  type="text"
                  className={`${inputClass} cursor-not-allowed opacity-50`}
                  disabled
                  placeholder={t("filters.monitoringLocation")}
                />
              )}
            </FormField>

            <FormField label={t("fields.permit")}>
              {facilityId ? (
                <Combobox
                  value={permitId}
                  onChange={setPermitId}
                  onSearch={setPermitSearch}
                  loading={permitsFetching}
                  placeholder={t("filters.permit")}
                  options={permits.map((p) => ({
                    value: p.id,
                    label: p.permit_number ?? p.id,
                  }))}
                />
              ) : (
                <input
                  type="text"
                  className={`${inputClass} cursor-not-allowed opacity-50`}
                  disabled
                  placeholder={t("filters.permit")}
                />
              )}
            </FormField>

            <FormField label={t("fields.eventType")} required>
              <Select
                value={eventType}
                onChange={(e) => setEventType(e.target.value as SamplingEventType)}
                options={eventTypeOptions}
              />
            </FormField>

            <FormField label={t("fields.assignedTo")} hint={t("fields.assignedToHint")}>
              <Combobox
                value={collectedBy}
                onChange={setCollectedBy}
                placeholder={t("fields.assignedTo")}
                options={members.map((m) => ({
                  value: m.profile_id,
                  label: m.full_name || m.email,
                }))}
              />
            </FormField>

            <FormField label={t("fields.scheduledDate")}>
              <input
                type="date"
                className={inputClass}
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            </FormField>
          </CardContent>
        </Card>

        {showCompliancePeriod && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <p className="text-sm font-medium text-on-surface">{t("compliancePeriod")}</p>
              <p className="text-xs text-muted">
                {scheduleId ? t("autoCalculated") : t("customPeriod")}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label={t("fields.compliancePeriodStart")}>
                  <input
                    type="date"
                    className={inputClass}
                    value={compliancePeriodStart}
                    onChange={(e) => setCompliancePeriodStart(e.target.value)}
                  />
                </FormField>
                <FormField label={t("fields.compliancePeriodEnd")}>
                  <input
                    type="date"
                    className={inputClass}
                    value={compliancePeriodEnd}
                    onChange={(e) => setCompliancePeriodEnd(e.target.value)}
                  />
                </FormField>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="space-y-4 pt-6">
            <FormField label={t("fields.weatherConditions")}>
              <input
                type="text"
                className={inputClass}
                value={weatherConditions}
                onChange={(e) => setWeatherConditions(e.target.value)}
              />
            </FormField>
            <FormField label={t("fields.fieldNotes")}>
              <textarea
                className={`${inputClass} min-h-[80px]`}
                value={fieldNotes}
                onChange={(e) => setFieldNotes(e.target.value)}
              />
            </FormField>
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!facilityId || !locationId || submitting}>
            {submitting ? t("saving", { ns: "common" }) : t("createTitle")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: "/sampling-events" })}
          >
            {t("cancel", { ns: "common" })}
          </Button>
        </div>
      </form>
    </div>
  );
}
